import { useState } from 'react';
import { ChevronLeft, ChevronRight, MapPin, Plus, Store, X } from 'lucide-react';
import { Button } from './Button';
import { DateLabel } from './DateLabel';
import { StatusBadge } from './WorkRecordModal';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { TimeRangeInput, computesCrossesMidnight, type TimeRangeInputValue } from './TimeRangeInput';
import { BreaksEditor, type BreakRow } from './BreaksEditor';
import { StoreSinglePicker, type StoreOption } from './StoreSinglePicker';
import { useAttendanceSettings } from '@/hooks/useAttendanceSettings';
import { useShiftPatternPresetsForNode } from '@/hooks/useShiftPatternPresetsForNode';
import { calcLaborMinutes, resolveAutoDeductBreakMinutes } from '@/lib/labor-hours';
import { buildLocalIso, isoToLocalHm } from '@/lib/shift-format';
import { addDays } from '@/lib/calendar-day';

/**
 * v0.4後半 月次実績一括入力 第2フェーズ I-4b (§6-2)「1日分のシート」。767px以下=下から開くシート /
 * 768〜1279px=右から重ねる Modal / 1280px以上=一覧+右パネル、のいずれの器からもこのコンポーネントが
 * 中身として使われる (器の切替は MonthlyAttendanceEntryPage 側の責務)。
 */
export type ShiftEntry = {
  key: string;
  workRecordId: string | null;
  workNodeId: string;
  plannedShiftPatternId: string | null;
  plannedTimeConfirmed: 0 | 1;
  time: TimeRangeInputValue;
  breakMinutes: string;
  breaks: BreakRow[];
  deviationReasonCodeId: string | null;
};
export type DayEntry = { reasonCodeId: string | null; shifts: ShiftEntry[] };

export type ReasonCode = { reason_code_id: string; category: string; code: string; label: string; sort_order: number };
type ExistingRecord = {
  work_record_id: string; status: 'draft' | 'submitted' | 'approved' | 'rejected';
  record_kind: 'worked' | 'absence'; start_at: string; end_at: string; break_minutes: number;
  reason_code_label: string | null; work_node_id: string;
};

const WORKED_OPTION = '__worked__';

/** ⚠️ production実機で発見(2026-09-28): `TimeRangeInput.applyPreset`は`onChange({time})`→
 *  `onPresetSelect(presetId)`の順に同期的に2回呼ぶが、呼び出し先の`updateShift`はレンダー間で
 *  不変な`entry` propを都度読んで丸ごと差し替えるため、2回目の呼び出しが1回目の時刻更新を無言で
 *  上書きしていた(パターンは選択されるが時刻欄が空のままになる)。この関数でpresetIdから時刻を
 *  引き直し、pattern・timeを1回のpatchに統合することで、呼び出し回数に依存しない安全な更新にする。 */
export const computePresetSelectionPatch = (
  presetId: string, presets: { id: string; startTime: string; endTime: string }[],
): Partial<ShiftEntry> => {
  const preset = presets.find((p) => p.id === presetId);
  return { plannedShiftPatternId: presetId, ...(preset ? { time: { startTime: preset.startTime, endTime: preset.endTime } } : {}) };
};

export const makeEmptyShift = (workNodeId: string, plannedShiftPatternId: string | null = null): ShiftEntry => ({
  key: `new-${Math.random().toString(36).slice(2)}`,
  workRecordId: null, workNodeId, plannedShiftPatternId, plannedTimeConfirmed: 0,
  time: { startTime: null, endTime: null }, breakMinutes: '0', breaks: [], deviationReasonCodeId: null,
});

const buildEndAtIso = (workDate: string, time: TimeRangeInputValue): string => {
  const endDate = computesCrossesMidnight(time) ? addDays(workDate, 1) : workDate;
  return buildLocalIso(endDate, time.endTime ?? '00:00');
};

type Props = {
  date: string;
  entry: DayEntry;
  onChange: (next: DayEntry) => void;
  locked: boolean;
  future: boolean;
  lockedRecords: ExistingRecord[];
  onNavigate: (deltaDays: number) => void;
  onCopyFromPreviousDay: () => void;
  canCopyFromPreviousDay: boolean;
  reasonCodes: ReasonCode[]; // category: absence/holiday_work (日単位の区分)
  deviationReasonCodes: ReasonCode[]; // category: late/early_leave (遅刻・早退の理由)
  allReasonCodesById: Map<string, ReasonCode>;
  storeOptions: StoreOption[];
  homeNodeId: string;
  recentStoreIds: string[];
  onStoreUsed: (nodeId: string) => void;
  /** 保存済み (workRecordId あり) の勤務を破棄する前に必ず await する (孤立行防止、設計書参照)。
   *  未保存 (workRecordId=null) の勤務に対しては呼び出し側で何もしない no-op でよい。 */
  onRemoveShift: (shift: ShiftEntry) => Promise<void>;
  holidayLabel?: string;
  onClose?: () => void;
};

export function MonthlyEntryDaySheet({
  date, entry, onChange, locked, future, lockedRecords, onNavigate, onCopyFromPreviousDay, canCopyFromPreviousDay,
  reasonCodes, deviationReasonCodes, allReasonCodesById, storeOptions, homeNodeId, recentStoreIds, onStoreUsed,
  onRemoveShift, holidayLabel, onClose,
}: Props) {
  const [activeShiftIdx, setActiveShiftIdx] = useState(0);
  const [pickerForShiftIdx, setPickerForShiftIdx] = useState<number | null>(null);
  const [confirmingRemoveShift, setConfirmingRemoveShift] = useState(false);
  const [removingShift, setRemovingShift] = useState(false);

  const isWorkedKind = (reasonCodeId: string | null) => reasonCodeId === null || allReasonCodesById.get(reasonCodeId)?.category === 'holiday_work';
  const workedKind = isWorkedKind(entry.reasonCodeId);
  const shift = entry.shifts[Math.min(activeShiftIdx, entry.shifts.length - 1)] ?? makeEmptyShift(homeNodeId);

  const { data: attendanceSettings } = useAttendanceSettings(shift.workNodeId, workedKind && !locked);
  const { presets: patternPresets } = useShiftPatternPresetsForNode(shift.workNodeId, workedKind && !locked);
  const isAutoDeductBreak = (attendanceSettings?.breakMode ?? 'clock') === 'auto_deduct';
  const isTimeRangeBreak = attendanceSettings?.breakRecordMode === 'time_range';

  const autoBreakMinutes = (() => {
    if (!shift.time.startTime || !shift.time.endTime) return null;
    const startAt = buildLocalIso(date, shift.time.startTime);
    const endAt = buildEndAtIso(date, shift.time);
    const durationMinutes = Math.round((new Date(endAt).getTime() - new Date(startAt).getTime()) / 60000);
    return resolveAutoDeductBreakMinutes(durationMinutes, attendanceSettings?.breakRules ?? []);
  })();
  const breakMinutesForLabor = isTimeRangeBreak
    ? shift.breaks.reduce((sum, b) => {
      if (!b.startTime || !b.endTime) return sum;
      const [sh, sm] = b.startTime.split(':').map(Number); const [eh, em] = b.endTime.split(':').map(Number);
      const s = (sh ?? 0) * 60 + (sm ?? 0); let e = (eh ?? 0) * 60 + (em ?? 0); if (e <= s) e += 24 * 60;
      return sum + (e - s);
    }, 0)
    : (isAutoDeductBreak ? (autoBreakMinutes ?? 0) : (Number(shift.breakMinutes) || 0));
  const laborMinutes = shift.time.startTime && shift.time.endTime
    ? calcLaborMinutes(buildLocalIso(date, shift.time.startTime), buildEndAtIso(date, shift.time), breakMinutesForLabor, attendanceSettings?.breakMode ?? 'clock', attendanceSettings?.breakRules ?? [])
    : null;

  const updateShift = (idx: number, patch: Partial<ShiftEntry>) => {
    onChange({ ...entry, shifts: entry.shifts.map((s, i) => (i === idx ? { ...s, ...patch } : s)) });
  };

  const setReasonCodeId = async (reasonCodeId: string | null) => {
    const nowWorked = isWorkedKind(reasonCodeId);
    if (nowWorked && entry.shifts.length === 0) {
      onChange({ reasonCodeId, shifts: [makeEmptyShift(homeNodeId)] });
    } else if (!nowWorked && entry.shifts.length > 1) {
      // ⚠️ design-and-code CRITICAL是正: 休みの区分は1行のみ (backend は日単位で1件しか許さない)。
      // 2勤務の日から休みへ切り替える際、2件目が既に保存済み (work_record_id あり) の行なら、
      // ローカル state から消すだけでは既存行が孤立し (AMBIGUOUS_MULTIPLE_RECORDS の恒久的な
      // 行き詰まりを生む、handoff文書参照)、先に onRemoveShift で確実に片付けてから切り替える。
      const removed = entry.shifts[1]!;
      setRemovingShift(true);
      try { await onRemoveShift(removed); } finally { setRemovingShift(false); }
      onChange({ reasonCodeId, shifts: entry.shifts.slice(0, 1) });
    } else {
      onChange({ ...entry, reasonCodeId });
    }
  };

  const addSecondShift = () => {
    const base = entry.shifts[0];
    onChange({ ...entry, shifts: [...entry.shifts, makeEmptyShift(base?.workNodeId ?? homeNodeId)] });
    setActiveShiftIdx(entry.shifts.length);
  };
  const removeSecondShift = async () => {
    const removed = entry.shifts[1]!;
    setRemovingShift(true);
    try { await onRemoveShift(removed); } finally { setRemovingShift(false); }
    onChange({ ...entry, shifts: entry.shifts.slice(0, 1) });
    setActiveShiftIdx(0);
    setConfirmingRemoveShift(false);
  };

  if (locked) {
    return (
      <div className="space-y-4">
        <SheetHeader date={date} holidayLabel={holidayLabel} onNavigate={onNavigate} onClose={onClose} />
        <div className="space-y-2">
          {lockedRecords.length === 0 ? (
            <p className="text-sm text-text-sub">この日の記録はありません。</p>
          ) : lockedRecords.map((r) => (
            <div key={r.work_record_id} className="border border-surface-border rounded-lg p-3 space-y-1 bg-surface-sunken/40">
              <div className="flex items-center gap-2"><StatusBadge status={r.status} /><span className="text-xs text-text-sub">{r.reason_code_label ?? '通常勤務'}</span></div>
              {r.record_kind === 'worked' && (
                <p className="text-xs text-text-sub tabular-nums">{isoToLocalHm(r.start_at)}〜{isoToLocalHm(r.end_at)}（休憩{r.break_minutes}分）</p>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <SheetHeader date={date} holidayLabel={holidayLabel} onNavigate={onNavigate} onClose={onClose} />

      {future ? (
        <p className="text-sm text-text-sub">未来日は入力できません。</p>
      ) : (
        <>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <Select value={entry.reasonCodeId ?? WORKED_OPTION} disabled={removingShift} onValueChange={(v) => { void setReasonCodeId(v === WORKED_OPTION ? null : v); }}>
              <SelectTrigger className="w-44 min-h-[44px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={WORKED_OPTION}>勤務</SelectItem>
                {reasonCodes.map((r) => <SelectItem key={r.reason_code_id} value={r.reason_code_id}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {canCopyFromPreviousDay && !locked && (
              <Button type="button" variant="secondary" size="sm" onClick={onCopyFromPreviousDay}>前日と同じ</Button>
            )}
          </div>

          {workedKind && (
            <>
              {entry.shifts.length === 2 && (
                <div className="flex items-center gap-1.5" role="tablist" aria-label="勤務の切替">
                  {[0, 1].map((i) => (
                    <button key={i} type="button" role="tab" aria-selected={activeShiftIdx === i} onClick={() => setActiveShiftIdx(i)}
                      className={`min-h-[44px] sm:min-h-[36px] px-3 rounded-full text-xs border ${activeShiftIdx === i ? 'bg-misespark-navy text-white border-misespark-navy' : 'border-surface-border text-text-body'}`}
                    >勤務{i + 1}</button>
                  ))}
                </div>
              )}

              <div className="space-y-3 border border-surface-border rounded-lg p-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <button type="button" onClick={() => setPickerForShiftIdx(activeShiftIdx)}
                    className="min-h-[44px] inline-flex items-center gap-1.5 px-2 rounded border border-surface-border text-sm hover:bg-surface-sunken"
                  ><Store size={14} className="shrink-0" />
                    <span>{storeOptions.find((o) => o.nodeId === shift.workNodeId)?.name ?? shift.workNodeId}</span>
                    <span className="text-text-sub text-xs">[変更]</span>
                  </button>
                  {shift.workNodeId !== homeNodeId && (
                    <span className="inline-flex items-center gap-1 text-[11px] text-text-sub px-1.5 py-0.5 rounded bg-surface-sunken">
                      <MapPin size={11} className="shrink-0" />所属外店舗
                    </span>
                  )}
                </div>

                <TimeRangeInput
                  value={shift.time}
                  onChange={(t) => updateShift(activeShiftIdx, { time: t })}
                  presets={patternPresets}
                  selectedPresetId={shift.plannedShiftPatternId ?? undefined}
                  onPresetSelect={(presetId) => updateShift(activeShiftIdx, computePresetSelectionPatch(presetId, patternPresets))}
                  legend="予定パターン・時刻"
                />

                {isTimeRangeBreak ? (
                  <BreaksEditor rows={shift.breaks} onChange={(rows) => updateShift(activeShiftIdx, { breaks: rows })} />
                ) : isAutoDeductBreak ? (
                  <p className="text-xs text-text-sub">休憩{autoBreakMinutes ?? 0}分（自動計算）</p>
                ) : (
                  <div className="flex items-center gap-1.5">
                    <label className="text-xs text-text-sub" htmlFor={`break-${date}-${activeShiftIdx}`}>休憩</label>
                    <input id={`break-${date}-${activeShiftIdx}`} type="number" min={0} value={shift.breakMinutes}
                      onChange={(e) => updateShift(activeShiftIdx, { breakMinutes: e.target.value })}
                      className="w-16 min-h-[44px] px-2 py-1 rounded border border-surface-border focus-ring appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" />
                    <span className="text-xs text-text-sub">分</span>
                  </div>
                )}

                <div>
                  <label className="block text-xs text-text-sub mb-1">遅刻・早退の理由（任意）</label>
                  <Select value={shift.deviationReasonCodeId ?? '__none__'} onValueChange={(v) => updateShift(activeShiftIdx, { deviationReasonCodeId: v === '__none__' ? null : v })}>
                    <SelectTrigger className="w-full min-h-[44px]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">なし</SelectItem>
                      {deviationReasonCodes.map((r) => <SelectItem key={r.reason_code_id} value={r.reason_code_id}>{r.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                {laborMinutes != null && <p className="text-xs text-text-sub tabular-nums">実働 {(laborMinutes / 60).toFixed(1)}h</p>}
              </div>

              {entry.shifts.length < 2 ? (
                <Button type="button" variant="secondary" size="sm" leftIcon={<Plus size={12} />} onClick={addSecondShift}>＋勤務を追加</Button>
              ) : activeShiftIdx === 1 && (
                confirmingRemoveShift ? (
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-text-sub">勤務2を削除しますか？</span>
                    <Button type="button" variant="danger" size="sm" loading={removingShift} disabled={removingShift} onClick={removeSecondShift}>削除する</Button>
                    <Button type="button" variant="secondary" size="sm" disabled={removingShift} onClick={() => setConfirmingRemoveShift(false)}>やめる</Button>
                  </div>
                ) : (
                  <button type="button" onClick={() => setConfirmingRemoveShift(true)}
                    className="min-h-[44px] inline-flex items-center gap-1 text-xs text-text-sub hover:text-brand-red"
                  ><X size={12} />勤務2を削除</button>
                )
              )}
            </>
          )}

          <div className="pt-2">
            <Button type="button" variant="primary" className="w-full" onClick={() => onNavigate(1)}>保存して翌日へ</Button>
          </div>
        </>
      )}

      {pickerForShiftIdx !== null && (
        <StoreSinglePicker
          open onClose={() => setPickerForShiftIdx(null)}
          options={storeOptions} homeNodeId={homeNodeId} recentNodeIds={recentStoreIds}
          onSelect={(nodeId) => {
            // §6-2 項目4: 店舗を変えたらパターンを外す。
            updateShift(pickerForShiftIdx, { workNodeId: nodeId, plannedShiftPatternId: null });
            onStoreUsed(nodeId);
          }}
        />
      )}
    </div>
  );
}

function SheetHeader({ date, holidayLabel, onNavigate, onClose }: { date: string; holidayLabel?: string; onNavigate: (d: number) => void; onClose?: () => void }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-1">
        <button type="button" aria-label="前日" onClick={() => onNavigate(-1)} className="min-h-[44px] min-w-[44px] flex items-center justify-center text-text-sub hover:text-text-body"><ChevronLeft size={18} /></button>
        <span className="text-sm font-bold"><DateLabel date={date} withWeekday /></span>
        <button type="button" aria-label="翌日" onClick={() => onNavigate(1)} className="min-h-[44px] min-w-[44px] flex items-center justify-center text-text-sub hover:text-text-body"><ChevronRight size={18} /></button>
      </div>
      <div className="flex items-center gap-2">
        {holidayLabel && <span className="text-xs text-brand-red">{holidayLabel}</span>}
        {onClose && <button type="button" aria-label="閉じる" onClick={onClose} className="min-h-[44px] min-w-[44px] flex items-center justify-center text-text-sub hover:text-text-body"><X size={18} /></button>}
      </div>
    </div>
  );
}
